import { Box } from '@mui/material';
import { useState, ChangeEvent, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useUser } from '../../hooks/useUser';
import { useAlert } from '../../hooks/useAlert';
import LayoutWithSidebar from '../../components/common/layoutWithSidebar';
import BrandManagmentHeader from '../../components/BrandManagement/BrandManagmentHeader';
import BrandManagmentTabs from '../../components/BrandManagement/BrandManagmentTabs';
import BrandDrawer from '../../components/UserJourny/FirstUserJourney/BrandDrawer';
import { ICreateBrand } from '../../schemas/apis/interface';
import {
  getAllBrandsApiV2,
  createBrandApi,
  updateBrandsApiV2,
  deleteBrandApi,
  getRestaurantApi,
} from '../../utils/apis';
import MessagePopup from '../../components/common/MessagePopup';
import BrandDetailsHeader from '../../components/StoreManagment/BrandDetails/BrandDetailsHeader';
import BrandDetailsTabs from '../../components/StoreManagment/BrandDetails/BrandDetailsTabs';

const BrandManagment = () => {
  const { user } = useUser();
  const queryClient = useQueryClient();
  const { alert, setAlert, showAlert } = useAlert();

  // Tab state
  const [selectedTab, setSelectedTab] = useState(0);

  // Brand state management
  const [selectedBrand, setSelectedBrand] = useState<any>(null);
  const [brandDrawerOpen, setBrandDrawerOpen] = useState(false);
  const [brandDetailsTab, setBrandDetailsTab] = useState(0);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [sameAsBusinessContact, setSameAsBusinessContact] = useState(false);

  const initialBrandState: ICreateBrand = {
    name: '',
    description: '',
    logo_url: '',
    website: '',
    poc_at_brand: '',
    poc_at_email: '',
    poc_contact: '',
    poc_role: '',
    primary_color: '',
    secondary_color: '',
    brand_guidelines_url: '',
    is_active: true,
    id: '',
  };

  const [brandData, setBrandData] = useState<ICreateBrand>(initialBrandState);

  // Queries
  const {
    data: brandsList,
    isLoading: brandsListLoading,
    isError: isBrandsListError,
    error: brandsListError,
  } = useQuery({
    queryKey: ['brands', user?.tenantName],
    queryFn: () => getAllBrandsApiV2(user?.tenantName || ''),
    staleTime: Infinity,
    gcTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 0,
    enabled: !!user?.tenantName,
  });

  // Add restaurants query for business contact info
  const { data: restaurants } = useQuery({
    queryKey: ['restaurants', user?.tenantName],
    queryFn: () => getRestaurantApi(user?.tenantName || ''),
    staleTime: Infinity,
    gcTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 0,
    enabled: !!user?.tenantName,
  });

  // Mutations
  const { mutateAsync: deleteBrandAsync, isPending: deleteBrandPending } =
    useMutation({
      mutationFn: (brandId: string) =>
        deleteBrandApi(brandId, user?.tenantName || ''),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['brands'] });
        showAlert('Brand deleted successfully', 'success');
        setSelectedBrand(null);
      },
      onError: () => {
        showAlert('Failed to delete brand', 'error');
      },
    });

  const { mutateAsync: createBrand, isPending: createBrandPending } =
    useMutation({
      mutationFn: ({ formData }: { formData: ICreateBrand }) =>
        createBrandApi(formData, user?.tenantName || ''),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['brands'] });
        showAlert('Brand saved successfully', 'success');
        handleBrandDrawerClose();
      },
      onError: () => {
        showAlert('Failed to save brand', 'error');
        handleBrandDrawerClose();
      },
    });

  const { mutateAsync: updateBrand, isPending: updateBrandPending } =
    useMutation({
      mutationFn: ({
        formData,
        brandId,
      }: {
        formData: ICreateBrand;
        brandId: string;
      }) => updateBrandsApiV2(formData, user?.tenantName || '', brandId),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['brands'] });
        showAlert('Brand updated successfully', 'success');
        handleBrandDrawerClose();
      },
      onError: () => {
        showAlert('Failed to update brand', 'error');
        handleBrandDrawerClose();
      },
    });

  // Handlers
  const handleBrandDrawerClose = () => {
    setBrandDrawerOpen(false);
    setIsFormSubmitted(false);

    if (!selectedBrand || (brandDrawerOpen && !selectedBrand.id)) {
      setSelectedBrand(null);
    }

    setBrandData(initialBrandState);
  };

  const handleAddBrand = () => {
    setBrandDrawerOpen(true);
  };

  const handleBrandSelect = (brand: any) => {
    setSelectedBrand(brand);
  };

  const handleBrandBack = () => {
    setSelectedBrand(null);
  };

  const handleEditBrand = (brand: any) => {
    if (selectedBrand) {
      setSelectedBrand(brand);
    }
    setBrandData(brand);
    setBrandDrawerOpen(true);
  };

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleBrandSave = async (
    e: React.MouseEvent<HTMLButtonElement> | React.FormEvent
  ) => {
    e.preventDefault();
    setIsFormSubmitted(true);

    if (!brandData.name?.trim()) {
      showAlert('Brand name is required', 'error');
      return;
    }

    if (!validateEmail(brandData.poc_at_email)) {
      showAlert('Please enter a valid email address', 'error');
      return;
    }

    const sanitizedData: ICreateBrand = {
      ...brandData,
      description: brandData.description || '',
      logo_url: brandData.logo_url || '',
      website: brandData.website || '',
      poc_role: brandData.poc_role || '',
      primary_color: brandData.primary_color || '',
      secondary_color: brandData.secondary_color || '',
      brand_guidelines_url: brandData.brand_guidelines_url || '',
    };

    try {
      if (brandData.id) {
        await updateBrand({
          formData: sanitizedData,
          brandId: brandData.id,
        });

        // Update the selectedBrand if we're in detail view
        if (selectedBrand) {
          setSelectedBrand({ ...selectedBrand, ...sanitizedData });
        }
      } else {
        await createBrand({
          formData: sanitizedData,
        });
      }
    } catch (error) {
      console.error('Error saving brand:', error);
      showAlert('Failed to save brand', 'error');
    }
  };

  const handleDeleteBrand = async (brand: any) => {
    if (brand?.id) {
      try {
        await deleteBrandAsync(brand.id.toString());
      } catch (error) {
        console.error('Error deleting brand:', error);
      }
    }
  };

  const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSameAsBusinessContact(event.target.checked);

    if (event.target.checked && restaurants?.data?.data[0]?.contacts) {
      const contactDetails = restaurants.data.data[0].contacts[0];
      setBrandData(prev => ({
        ...prev,
        poc_at_brand: contactDetails.poc_name || '',
        poc_at_email: contactDetails.poc_email || '',
        poc_role: contactDetails.designation || '',
        poc_contact: contactDetails.poc_phone || '',
      }));
    }
  };

  // Update selected brand when brand list refreshes
  useEffect(() => {
    if (selectedBrand && brandsList?.data?.data.length > 0) {
      // Find the updated brand data from the refreshed list
      const updatedBrand = brandsList?.data?.data.find(
        (brand: any) => brand.id === selectedBrand.id
      );

      // Update the selectedBrand state with the latest data if found
      if (updatedBrand) {
        setSelectedBrand(updatedBrand);
      }
    }
  }, [brandsList, selectedBrand?.id, selectedBrand]);

  return (
    <LayoutWithSidebar margin={2}>
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
        }}
      >
        {selectedBrand ? (
          <>
            <BrandDetailsHeader
              brandName={selectedBrand.name}
              onBack={handleBrandBack}
              onEdit={() => handleEditBrand(selectedBrand)}
              onDelete={() => handleDeleteBrand(selectedBrand)}
            />
            <BrandDetailsTabs
              brand={selectedBrand}
              selectedTab={brandDetailsTab}
              onTabChange={setBrandDetailsTab}
            />
          </>
        ) : (
          <>
            <BrandManagmentHeader
              selectedTab={selectedTab}
              onAddClick={handleAddBrand}
            />
            <BrandManagmentTabs
              selectedTab={selectedTab}
              setSelectedTab={setSelectedTab}
              brandsList={brandsList?.data?.data || []}
              brandsListLoading={brandsListLoading}
              isBrandsListError={isBrandsListError}
              brandsListError={brandsListError}
              onEditBrand={handleEditBrand}
              onBrandSelect={handleBrandSelect}
              onDeleteBrand={handleDeleteBrand}
              deletePending={deleteBrandPending}
            />
          </>
        )}

        <BrandDrawer
          open={brandDrawerOpen}
          onClose={handleBrandDrawerClose}
          brandData={brandData}
          setBrandData={setBrandData}
          onSave={handleBrandSave}
          sameAsBusinessContact={sameAsBusinessContact}
          setSameAsBusinessContact={setSameAsBusinessContact}
          handleCheckboxChange={handleCheckboxChange}
          isFormSubmitted={isFormSubmitted}
          isPending={createBrandPending || updateBrandPending}
          validateEmail={validateEmail}
        />

        <MessagePopup alert={alert} setAlert={setAlert} />
      </Box>
    </LayoutWithSidebar>
  );
};

export default BrandManagment;
